import axios from "axios";
import {useCallback, useEffect, useRef, useState, useContext} from "react";
import {parseJwt} from "../utils/jwt";
import {v4 as uuid} from "uuid";
import {useNavigate} from "react-router-dom";
import {useCookies} from "react-cookie";
import {DialogContext, DialogType} from "avrora";

axios.interceptors.request.use(function (config) {
  config.headers['X-Cryptomail-Trace-Id'] = uuid();

  return config;
}, function (error) {
  return Promise.reject(error);
});

export const useAuth = () => {
  const [user, setUser] = useState<any>();

  const refreshingPromise = useRef<Promise<void> | null>(null);

  const token = useRef(localStorage.getItem('token'));
  const refreshToken = useRef(localStorage.getItem('refreshToken'));
  const [cookies, setCookie, removeCookie, updateCookies] = useCookies(['accessToken']);

  const configure = useCallback(() => {
    token.current = localStorage.getItem('token');
    refreshToken.current = localStorage.getItem('refreshToken');
    
    updateCookies()
    if (cookies.accessToken) {
      const tokens = JSON.parse(atob(cookies.accessToken));
      removeCookie("accessToken");
      cookies.accessToken = null;
      set(tokens.auth, tokens.refresh);
      token.current = tokens.auth ?? null;
      refreshToken.current = tokens.refresh ?? null;
    }
    axios.defaults.headers.common.Authorization = token.current ? `Bearer ${token.current}` : undefined;

    axios.interceptors.response.clear();
    
    if (refreshToken.current) {
      axios.interceptors.response.use(async (response) => {
        return response;
      }, err => {
        if (err && err.response?.status === 401 && !err.config.__retry) {
          if (!refreshingPromise.current) {
            refreshingPromise.current = new Promise((resolve, reject) => {
              (new axios.Axios(err.config)).post('/eus/token/refresh', null, {headers: {Authorization: `Bearer ${refreshToken.current}`}}).then(resp => {
                set(resp.data.auth, resp.data.refresh);
                configure();
                refreshingPromise.current = null;
                resolve();
              }).catch(err => {
                set();
                configure();
                refreshingPromise.current = null;
                reject();
              })
            });
          }

          return refreshingPromise.current.then(() => {
            return axios.request({
              ...err.config,
              headers: {...err.config.headers, "Authorization": `Bearer ${token.current}`},
              __retry: true
            } as any);
          });
        }

        return Promise.reject(err);
      })
    }

    setUser(token.current ? parseJwt(token.current) : null);
  }, [cookies, updateCookies, removeCookie]);

  const login = useCallback((username: string, password: string, secretCode: string, accessToken?: string) => {
    try {
      const base64 = btoa(`${username}:${password}`);

      if (secretCode) {
        const authHeader = accessToken ? `Bearer ${accessToken}` : `Basic ${base64}`;
        
        const config: any = {
          headers: {}
        };
        
        config.headers['Authorization'] = authHeader;
        config.headers['Accept'] = 'application/json';
        
        return axios.post('/eus/twofa', { code: secretCode }, config).then((resp: any) => {
          set(resp.data.auth, resp.data.refresh);
          token.current = resp.data.auth;
          refreshToken.current = resp.data.refresh;
          configure();
          return Promise.resolve();
        });
      }

      return axios.post('/eus/auth/basic', null, {
        headers: {
          Authorization: `Basic ${base64}`,
          Accept: "application/json"
        }
      }).then((resp: any) => {
        if (resp.data?.auth && resp.data?.refresh) {
          set(resp.data.auth, resp.data.refresh);
          token.current = resp.data.auth;
          refreshToken.current = resp.data.refresh;
          configure();
          return Promise.resolve();
        } else {
          return Promise.reject({ response: { status: 409, data: { message: "verification code required" } } });
        }
      });
    } catch (e) {
      return Promise.reject(e);
    }
  }, [configure]);

  const logout = useCallback(() => {
    set();
    configure();
  }, []);

  const dialogContext = useContext(DialogContext);

  const register = useCallback((username: string,
                                password: string,
                                password2: string,
                                firstName: string,
                                secondName: string,
                                surname: string,
                                phone: string,
  ) => {
    return axios.post('/eus/auth/register', {
      email: username,
      firstName,
      secondName,
      surname,
      phone,
      password,
      password2
    }).catch((error) => {
      dialogContext.push({
        text: "Нет лицензии",
        title: "Ошибка регистрации",
        type: DialogType.Error,
      });
      return Promise.reject(error);
    });
  }, [dialogContext]);

  const check = useCallback(async (email: string): Promise<{
    can_register?: boolean;
    two_fa_enabled?: boolean,
    status?: 'active' | 'blocked' | 'suspended' | 'not_confirmed' | 'invited' | 'awaiting_manual_check'
    auth_url?: string;
  }> => {
    const response = await axios.get('/eus/user/check?email=' + email).then(resp => resp.data);
    if (response?.can_register === false) {
      dialogContext.push({
        text: "Извините, данный адрес почты не может быть зарегистрирован в системе. Обратитесь к администратору support@cybercoachman.ru",
        type: DialogType.Error,
      });
    }

    return response;
  }, [dialogContext]);

  const set = useCallback((auth?: string, refresh?: string) => {
    token.current = auth ?? null;
    token.current ? localStorage.setItem('token', token.current) : localStorage.removeItem('token');
    refreshToken.current = refresh ?? null;
    refreshToken.current ? localStorage.setItem('refreshToken', refreshToken.current) : localStorage.removeItem('refreshToken');
  }, []);

  const navigate = useNavigate();

  useEffect(() => {
    configure();

    if (token.current) {
      const exp = parseJwt(token.current)?.exp * 1000;

      if (exp <= new Date().valueOf()) {
        if (refreshToken.current && parseJwt(refreshToken.current).exp * 1000 > new Date().valueOf()) {
          axios.post('/eus/token/refresh', null, {headers: {Authorization: `Bearer ${refreshToken.current}`}}).then(resp => {
            set(resp.data.auth, resp.data.refresh);
            configure();
          }).catch(() => {
            set();
            configure();
            navigate('/')
          })
        } else {
          set();
          configure();
          navigate('/')
        }
      }
    }
  }, []);

  const reset2FA = useCallback(() => {
    return axios.post('/eus/twofa-reset');
  }, []);

  return {
    user,
    register,
    check,
    login,
    logout,
    reset2FA
  }
}
